// User-facing error text for sign-in, account and membership calls. Ports three iOS pieces so the console says what
// the app says:
// - `SupabaseError.errorDescription` (GroundTeam/Sync/Supabase/SupabaseClient.swift): GoTrue error codes, RPC
//   `ServerErrorKind`s and Edge Function refusals;
// - `TeamSyncMessages.message(for:companyName:)` (GroundTeam/Views/Sync/SyncStatusBadge.swift): friendlier wording
//   for a few server kinds on the team screens;
// - `AuthErrorText.message(for:)` (GroundTeam/Views/Sync/Auth/AuthFormFields.swift): a cancelled Apple / Google
//   sheet is the person's choice, so it stays silent (null).
// Pure module (no env, no supabase client) so it is unit-tested in node.

import type { ServerErrorKind } from '@/contracts/wire';

/** A sign-in popup (Apple / Google) that didn't produce a token. `cancelled` is silent. */
export class AuthFlowError extends Error {
  readonly kind: 'cancelled' | 'failed';
  constructor(kind: 'cancelled' | 'failed', message = kind === 'cancelled' ? 'Cancelled' : 'Sign-in failed') {
    super(message);
    this.name = 'AuthFlowError';
    this.kind = kind;
  }
}

/**
 * An HTTP error from PostgREST (RPC), GoTrue or an Edge Function, normalised like iOS `SupabaseError`:
 * `.http` (code = SQLSTATE, GoTrue error code or a server kind; message = the server text) or, with `refused`,
 * `.refused` (an Edge Function's own `{"error": code, "message": text}` answer, whose names can mean something else
 * than the same words from an RPC).
 */
export class ServerError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly refused: boolean;
  constructor(status: number, code: string | null, message: string, refused = false) {
    super(message);
    this.name = 'ServerError';
    this.status = status;
    this.code = code;
    this.refused = refused;
  }
}

/**
 * delete-account's 409 `{ok: false, reason: 'last_admin', company}`: the caller is the only admin of a company that
 * still has other members. `company` is the name the function sent (null when it sent none).
 */
export class LastAdminError extends ServerError {
  readonly company: string | null;
  constructor(company: string | null) {
    super(409, 'last_admin', 'last_admin');
    this.name = 'LastAdminError';
    this.company = company;
  }
}

/** The session was refused and refreshing it failed: the person has to sign in again. */
export class SessionExpiredError extends Error {
  constructor() {
    super(SESSION_EXPIRED_TEXT);
    this.name = 'SessionExpiredError';
  }
}

/** The request never got an answer (offline, DNS, CORS, TLS). */
export class NetworkError extends Error {
  constructor(message = 'Network request failed') {
    super(message);
    this.name = 'NetworkError';
  }
}

// ---------------------------------------------------------------------------------------------------------------------
// Texts (word for word from iOS unless noted)
// ---------------------------------------------------------------------------------------------------------------------

export const WEAK_PASSWORD_TEXT = 'Use at least 12 characters with upper- and lowercase letters, a number and a symbol.';
export const PWNED_PASSWORD_TEXT = 'This password has appeared in a data breach. Choose a different one.';
export const SESSION_EXPIRED_TEXT = 'Your session expired. Please sign in again.';
export const ACCOUNT_EXISTS_TEXT = 'An account with this email already exists. Sign in instead, or reset your password.';
/**
 * WEB_PLAN.md §4.2: the sign-up code confirmed the account but saving its password failed twice. (iOS says "Set one
 * in Settings › Team Sync › Change password", a screen the browser doesn't have while signed out.)
 */
export const PASSWORD_NOT_SAVED_TEXT = "Your email is confirmed but the password wasn't saved. Set it with Forgot password.";
/** Web wording: iOS's offline text talks about changes syncing later, which doesn't fit a sign-in form. */
export const OFFLINE_TEXT = "You're offline. Connect to the internet, then try again.";
export const UNREACHABLE_TEXT = "Couldn't reach the server. Check your connection and try again.";

const SERVER_KINDS: readonly ServerErrorKind[] = [
  'not_member',
  'suspended',
  'forbidden',
  'last_admin',
  'invalid_input',
  'already_member',
  'no_invitation',
  'invalid_code',
  'invitation_expired',
  'too_many_invitations',
  'company_creation_disabled',
];

function isServerKind(value: string): value is ServerErrorKind {
  return (SERVER_KINDS as readonly string[]).includes(value);
}

function record(e: unknown): Record<string, unknown> | null {
  return typeof e === 'object' && e !== null ? (e as Record<string, unknown>) : null;
}

function text(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const t = value.trim();
  return t === '' ? null : t;
}

function statusOf(e: unknown): number | null {
  const s = record(e)?.status;
  return typeof s === 'number' ? s : null;
}

// ---------------------------------------------------------------------------------------------------------------------
// Classification
// ---------------------------------------------------------------------------------------------------------------------

/**
 * The GoTrue / server error code of an error ("email_not_confirmed", "same_password", "42501"…): supabase-js
 * `AuthError.code`, or `ServerError.code`. Null for transport errors.
 */
export function authErrorCode(e: unknown): string | null {
  const r = record(e);
  if (!r) return null;
  return text(r.code) ?? text(r.error_code);
}

/** The server condition an RPC error names (message first, then code), like `SupabaseError.serverKind`. */
export function serverErrorKind(e: unknown): ServerErrorKind | null {
  if (e instanceof ServerError && e.refused) return null;
  const r = record(e);
  if (!r) return null;
  const message = text(r.message);
  if (message && isServerKind(message)) return message;
  const code = text(r.code);
  if (code && isServerKind(code)) return code;
  return null;
}

/** A cancelled Apple / Google popup, or an aborted request: silent. */
export function isCancelled(e: unknown): boolean {
  if (e instanceof AuthFlowError) return e.kind === 'cancelled';
  const r = record(e);
  return r?.name === 'AbortError';
}

/** What browsers (and Node) say when fetch got no answer: Chrome, Firefox, Safari, Node, React Native. */
const FETCH_FAILED = /failed to fetch|networkerror|network request failed|load failed|fetch failed/i;

/**
 * No answer from the server: fetch's "Failed to fetch" TypeError, supabase-js `AuthRetryableFetchError` with status
 * 0, `FunctionsFetchError`, PostgREST's status-0 error object, or our NetworkError. Any other TypeError is a bug, not
 * a connection problem, and must not be retried or worded as one.
 */
export function isNetworkError(e: unknown): boolean {
  if (e instanceof NetworkError) return true;
  if (e instanceof ServerError) return false;
  const r = record(e);
  if (!r) return false;
  const name = typeof r.name === 'string' ? r.name : '';
  if (name === 'FunctionsFetchError') return true;
  if (name === 'AuthRetryableFetchError') return statusOf(e) === 0 || statusOf(e) === null;
  const message = typeof r.message === 'string' ? r.message : '';
  return FETCH_FAILED.test(message) && (statusOf(e) === 0 || statusOf(e) === null);
}

/** The person has to sign in again (iOS `isAuthFailure`). */
export function isSessionFailure(e: unknown): boolean {
  if (e instanceof SessionExpiredError) return true;
  const r = record(e);
  if (r?.name === 'AuthSessionMissingError') return true;
  return statusOf(e) === 401 && !(e instanceof ServerError && e.refused);
}

function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

// ---------------------------------------------------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------------------------------------------------

/** GoTrue error codes worth a plain explanation (SupabaseError.errorDescription, `.http` without a server kind). */
function goTrueText(code: string, e: unknown): string | null {
  switch (code) {
    case 'user_already_exists':
    case 'email_exists':
      return ACCOUNT_EXISTS_TEXT;
    case 'phone_exists':
      return 'An account with this phone number already exists. Sign in instead, or reset your password.';
    case 'signup_disabled':
      return 'New accounts are turned off on this server.';
    case 'otp_disabled':
      return "There's no account with this email or number yet. Create an account first.";
    case 'sms_send_failed':
    case 'phone_provider_disabled':
      return "Text messages aren't set up yet. Use email, Apple or Google for now.";
    case 'provider_disabled':
    case 'oauth_provider_not_supported':
      return "That sign-in option isn't turned on yet. Try another one.";
    case 'email_provider_disabled':
      return "Email sign-up isn't turned on yet. Try another option.";
    case 'email_not_confirmed':
      return 'Confirm your email first with the code we sent.';
    case 'phone_not_confirmed':
      return 'Confirm your phone number first with the code we texted.';
    case 'bad_code_verifier':
    case 'flow_state_not_found':
    case 'flow_state_expired':
      return 'Sign-in took too long. Please try again.';
    case 'email_address_not_authorized':
      return "We couldn't email this address yet (the server's email sender isn't set up). Try Apple, Google or phone.";
    case 'over_email_send_rate_limit':
    case 'over_sms_send_rate_limit':
    case 'over_request_rate_limit':
      return 'Too many attempts. Wait a minute and try again.';
    case 'otp_expired':
      return 'That code is wrong or has expired. Request a new one.';
    case 'invalid_credentials':
      return 'The email, phone number or password is incorrect.';
    case 'weak_password': {
      // GoTrue's own text lists every allowed symbol; ours is shorter. A leaked password (reason "pwned") can't be
      // fixed by the rules, so it gets its own text.
      const reasons = record(e)?.reasons;
      const pwned = Array.isArray(reasons) && reasons.includes('pwned');
      return pwned ? PWNED_PASSWORD_TEXT : WEAK_PASSWORD_TEXT;
    }
    case 'same_password':
      return "Choose a password you haven't used for this account.";
    case 'reauthentication_needed':
      return 'For your security, sign out and sign in again, then change your password.';
    default:
      return null;
  }
}

/** `SupabaseError.errorDescription` for `.http` with a server kind. */
function serverKindText(kind: ServerErrorKind, message: string): string {
  switch (kind) {
    case 'not_member':
      return "You're no longer a member of this company.";
    case 'suspended':
      return 'Your account has been suspended by an admin.';
    case 'forbidden':
      return "You don't have permission to do that.";
    case 'invalid_input':
      return "Some of the information entered isn't valid.";
    case 'last_admin':
      return message === 'last_admin'
        ? 'The company needs at least one active admin. Make someone else an admin first.'
        : message;
    case 'already_member':
      return 'This account is already in a company, or someone already joined with this invitation.';
    case 'company_creation_disabled':
      return 'Creating new companies is turned off on this server. Ask your admin for an invitation.';
    case 'no_invitation':
      return "That invitation isn't available anymore. Ask your admin to invite you again.";
    case 'invalid_code':
      return "That invite code isn't valid. Check it with your admin.";
    case 'invitation_expired':
      return 'This invitation has expired. Ask your admin for a new one.';
    case 'too_many_invitations':
      return 'Your company has too many open invitations. Revoke some in Team members first.';
  }
}

/** `.refused` (Edge Function) wording. */
function refusedText(e: ServerError): string {
  const m = e.message.trim();
  switch (e.code) {
    case 'weak_password':
      return m === '' || m === e.code ? WEAK_PASSWORD_TEXT : m;
    case 'forbidden':
      return "You don't have permission to do that.";
    case 'invalid_input':
      return "Some of the information entered isn't valid.";
    case NOT_DEPLOYED_CODE:
      return 'The team server needs an update. Ask whoever set it up to deploy the latest functions.';
    default:
      return m === '' || m === e.code ? `The server couldn't do that (${e.status}).` : m;
  }
}

/** `.refused` code for a function that isn't deployed (HTTP 404 from the gateway without an error name). */
export const NOT_DEPLOYED_CODE = 'not_deployed';

/** Port of `SupabaseError.errorDescription` for whatever supabase-js (or our wrappers) threw. */
export function supabaseErrorText(e: unknown): string {
  if (e instanceof SessionExpiredError) return SESSION_EXPIRED_TEXT;
  if (isNetworkError(e)) return isOffline() ? OFFLINE_TEXT : UNREACHABLE_TEXT;
  if (e instanceof ServerError && e.refused) return refusedText(e);
  const r = record(e);
  const message = text(r?.message) ?? '';
  const kind = serverErrorKind(e);
  if (kind) return serverKindText(kind, message);
  const code = authErrorCode(e);
  if (code) {
    const known = goTrueText(code, e);
    if (known) return known;
  }
  const status = statusOf(e);
  if (r?.name === 'AuthSessionMissingError') return SESSION_EXPIRED_TEXT;
  if (status === 429) return 'Too many requests. Please wait a moment and try again.';
  if (message !== '') return message;
  return status ? `Server error (${status}).` : 'Something went wrong. Please try again.';
}

/**
 * Port of `TeamSyncMessages.message(for:companyName:)`: team screens (account, leave company, delete account) with a
 * few server kinds worded for the person doing it.
 */
export function teamErrorMessage(e: unknown, companyName?: string | null): string {
  switch (serverErrorKind(e)) {
    case 'last_admin': {
      const company = companyName ? ` of ${companyName}` : '';
      return `You're the only admin${company}. Make another teammate an admin first (Team › edit their profile › Role), or remove the other members.`;
    }
    case 'already_member':
      return 'This account is already in a company, or someone already joined with this invitation.';
    case 'company_creation_disabled':
      return 'Creating new companies is turned off on this server. Ask your admin to invite you instead.';
    case 'forbidden':
      return "You don't have permission to do that. Ask an admin.";
    case 'not_member':
    case 'suspended':
      return "You're no longer an active member of this company.";
    default:
      return supabaseErrorText(e);
  }
}

/** Sign-in screens (`AuthErrorText.message(for:)`): null for a cancelled popup, else the team wording. */
export function authErrorMessage(e: unknown): string | null {
  if (isCancelled(e)) return null;
  if (e instanceof AuthFlowError) return e.message;
  return teamErrorMessage(e);
}

/** Supabase Auth refused because a sign-in option or sign-ups are off (the sign-in screen re-reads its options). */
export function isOptionTurnedOff(e: unknown): boolean {
  const code = authErrorCode(e);
  return code !== null
    && ['provider_disabled', 'oauth_provider_not_supported', 'phone_provider_disabled', 'email_provider_disabled',
      'signup_disabled'].includes(code);
}
